/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APR.ANALYTICS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApprovalAnalyticsPage.tsx. A DISTINCT
 * presentation (KPI tiles + scannable breakdown cards + bottom-sheet detail) over
 * the SAME read-only data layer — never the desktop grid re-hosted (§16):
 *   - reads via apiDb over the engine's security_invoker metric views (E4):
 *       apr_config_metrics_v   — per-config throughput + avg cycle time
 *       apr_approver_workload_v — per-approver pending / decided counts
 *     RLS scopes what each user sees (the gateway forwards the JWT).
 *   - resolves each approver FK (approver_user_id) to a real name via
 *     csm_user_master, so no raw surrogate id ever reaches render (§17.B4).
 * This screen is READ-ONLY: it performs no writes, so it imports neither
 * routed_writes nor any mutation path (§2 — there is simply nothing to write).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Layers, UserRound, Clock } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Row shapes (mirror the desktop view columns exactly — do not invent columns).
// ---------------------------------------------------------------------------

interface ConfigMetric {
  approval_config_id: number;
  config_code: string;
  entity_name: string;
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  avg_cycle_seconds: number | null;
}

interface Workload {
  approver_user_id: number;
  pending_steps: number;
  approved_steps: number;
  rejected_steps: number;
}

/** A workload row enriched with the approver's resolved display name. */
interface WorkloadRow extends Workload {
  approver_name: string;
}

type ViewFacet = 'configs' | 'approvers';

/** Discriminated selection for the detail bottom-sheet. */
type Selected =
  | { kind: 'config'; row: ConfigMetric }
  | { kind: 'approver'; row: WorkloadRow };

// ---------------------------------------------------------------------------
// Duration formatting — avg_cycle_seconds is an elapsed span, NOT a date, so
// format_date does not apply here (mirrors the desktop humanSeconds helper).
// ---------------------------------------------------------------------------

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

function humanSeconds(s: number | null): string {
  if (s == null) return '—';
  if (s < SECONDS_PER_HOUR) return `${Math.round(s / 60)}m`;
  if (s < SECONDS_PER_DAY) return `${(s / SECONDS_PER_HOUR).toFixed(1)}h`;
  return `${(s / SECONDS_PER_DAY).toFixed(1)}d`;
}

export function MobileApprovalAnalyticsPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ConfigMetric[]>([]);
  const [workload, setWorkload] = useState<WorkloadRow[]>([]);
  const [view, setView] = useState<ViewFacet>('configs');
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Selected | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [c, w] = await Promise.all([
        apiDb
          .from('apr_config_metrics_v')
          .select('approval_config_id, config_code, entity_name, total, pending, approved, rejected, avg_cycle_seconds')
          .order('total', { ascending: false }),
        apiDb
          .from('apr_approver_workload_v')
          .select('approver_user_id, pending_steps, approved_steps, rejected_steps')
          .order('pending_steps', { ascending: false }),
      ]);
      if (c.error) throw c.error;
      if (w.error) throw w.error;

      setConfigs((c.data ?? []) as ConfigMetric[]);

      const wl = (w.data ?? []) as Workload[];
      // Resolve every approver FK to a real name so no surrogate id reaches
      // render (§17.B4). One batched lookup, then map over the workload rows.
      const ids = Array.from(
        new Set(wl.map((x) => x.approver_user_id).filter((x): x is number => x != null)),
      );
      const names: Record<number, string> = {};
      if (ids.length) {
        const { data: us, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', ids);
        if (uErr) throw uErr;
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          names[u.user_id] = u.display_name || u.email || '—';
        }
      }
      setWorkload(
        wl.map((x) => ({ ...x, approver_name: names[x.approver_user_id] ?? '—' })),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  // Aggregate KPI totals across all configurations (§ HomeScreen KPI pattern).
  const totals = useMemo(() => {
    return configs.reduce(
      (acc, c) => ({
        total: acc.total + (c.total ?? 0),
        pending: acc.pending + (c.pending ?? 0),
        approved: acc.approved + (c.approved ?? 0),
        rejected: acc.rejected + (c.rejected ?? 0),
      }),
      { total: 0, pending: 0, approved: 0, rejected: 0 },
    );
  }, [configs]);

  const filteredConfigs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return configs;
    return configs.filter(
      (c) => c.config_code.toLowerCase().includes(q) || c.entity_name.toLowerCase().includes(q),
    );
  }, [configs, filter]);

  const filteredWorkload = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return workload;
    return workload.filter((w) => w.approver_name.toLowerCase().includes(q));
  }, [workload, filter]);

  const viewFacets: { key: ViewFacet; label: string }[] = [
    { key: 'configs', label: t('mobile.apr_analytics.by_configuration', 'By configuration') },
    { key: 'approvers', label: t('mobile.apr_analytics.approver_workload', 'Approver workload') },
  ];

  const isConfigView = view === 'configs';
  const emptyList = isConfigView ? filteredConfigs.length === 0 : filteredWorkload.length === 0;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.apr_analytics.title', 'Approval Analytics')}
        description={t('mobile.apr_analytics.description', 'Throughput by configuration and the workload each approver carries.')}
      />

      <MobileStatStrip
        stats={[
          { value: totals.total.toLocaleString(), label: t('mobile.apr_analytics.kpi.total', 'Total') },
          { value: totals.pending.toLocaleString(), label: t('mobile.apr_analytics.kpi.pending', 'Pending') },
          { value: totals.approved.toLocaleString(), label: t('mobile.apr_analytics.kpi.approved', 'Approved') },
          { value: totals.rejected.toLocaleString(), label: t('mobile.apr_analytics.kpi.rejected', 'Rejected') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={
            isConfigView
              ? t('mobile.apr_analytics.search_configs', 'Search configurations')
              : t('mobile.apr_analytics.search_approvers', 'Search approvers')
          }
          aria-label={
            isConfigView
              ? t('mobile.apr_analytics.search_configs', 'Search configurations')
              : t('mobile.apr_analytics.search_approvers', 'Search approvers')
          }
        />
      </div>

      <MobileFilterChips
        chips={viewFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => view === v}
        onSelect={(v) => { setView(v as ViewFacet); setFilter(''); }}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {emptyList && !busy ? (
        <MobileContentCard>
          <div className="m-empty"><Inbox /> {t('mobile.apr_analytics.none', 'No data')}</div>
        </MobileContentCard>
      ) : isConfigView ? (
        <MobileContentCard list>
          {filteredConfigs.map((c) => (
            <MobileCardRow
              key={c.approval_config_id}
              lead={<Layers />}
              leadTone={c.pending > 0 ? 'amber' : 'slate'}
              /* fk-display-ok: config_code is the analytics row's own natural APR identifier (no separate name) */
              title={c.config_code}
              subtitle={`${c.entity_name} · ${t('mobile.apr_analytics.total_n', { count: c.total, defaultValue: `${c.total} total` })}`}
              trailing={
                <MobileStatusPill tone={c.pending > 0 ? 'wait' : 'ok'}>
                  {t('mobile.apr_analytics.pending_n', { count: c.pending, defaultValue: `${c.pending} pending` })}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected({ kind: 'config', row: c })}
            />
          ))}
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filteredWorkload.map((w) => (
            <MobileCardRow
              key={w.approver_user_id}
              lead={<UserRound />}
              leadTone={w.pending_steps > 0 ? 'amber' : 'green'}
              title={w.approver_name}
              subtitle={t('mobile.apr_analytics.decided_n', {
                count: w.approved_steps + w.rejected_steps,
                defaultValue: `${w.approved_steps + w.rejected_steps} decided`,
              })}
              trailing={
                <MobileStatusPill tone={w.pending_steps > 0 ? 'wait' : 'ok'}>
                  {t('mobile.apr_analytics.pending_n', { count: w.pending_steps, defaultValue: `${w.pending_steps} pending` })}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected({ kind: 'approver', row: w })}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only breakdown) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.apr_analytics.detail', 'Analytics detail')}>
        <div className="m-grabber" />
        {selected?.kind === 'config' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Layers size={20} /></span>
              <span>
                <div className="m-nm">{selected.row.config_code}</div>
                <div className="m-em">{selected.row.entity_name}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.total', 'Total')} value={selected.row.total.toLocaleString()} />
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.pending', 'Pending')} value={<MobileStatusPill tone="wait">{selected.row.pending.toLocaleString()}</MobileStatusPill>} />
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.approved', 'Approved')} value={<MobileStatusPill tone="ok">{selected.row.approved.toLocaleString()}</MobileStatusPill>} />
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.rejected', 'Rejected')} value={<MobileStatusPill tone="no">{selected.row.rejected.toLocaleString()}</MobileStatusPill>} />
              <MobileDetailRow
                label={t('mobile.apr_analytics.avg_cycle', 'Avg cycle')}
                value={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> {humanSeconds(selected.row.avg_cycle_seconds)}</span>}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
        {selected?.kind === 'approver' && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><UserRound size={20} /></span>
              <span>
                <div className="m-nm">{selected.row.approver_name}</div>
                <div className="m-em">{t('mobile.apr_analytics.approver_workload', 'Approver workload')}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.pending', 'Pending')} value={<MobileStatusPill tone="wait">{selected.row.pending_steps.toLocaleString()}</MobileStatusPill>} />
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.approved', 'Approved')} value={<MobileStatusPill tone="ok">{selected.row.approved_steps.toLocaleString()}</MobileStatusPill>} />
              <MobileDetailRow label={t('mobile.apr_analytics.kpi.rejected', 'Rejected')} value={<MobileStatusPill tone="no">{selected.row.rejected_steps.toLocaleString()}</MobileStatusPill>} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
